import { useState, useEffect } from "react";
import "admin-lte/dist/css/adminlte.min.css";
import apiManager from "../../api/apiManager";
import API_ENDPOINTS from "../../api/apiEndpoints";
import { useMsal } from "@azure/msal-react";
import { useSelectedDealership } from "../../hooks/useLocalStorage";
import { useLocation, useNavigate } from "react-router-dom";
import { showErrorMessage } from "../../shared/Services/toasterService";
import { useLocalization } from "../../context/LocalizationContext";
import { useRef } from "react";
import { US_STATES } from "../../shared/helpers/constants";
import getUserRoleFromJWT from "../../shared/helpers/getUserRoleFromJWT";
import { UserRole } from "../../shared/Enums/UserRole";

const Header = () => {
  const LOGO_URL = process.env.REACT_APP_LOGIN_LOGO_URL || "/assets/images/logo/AutoMenuLogo.png";
  const { t: localization } = useLocalization();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealershipId, setSelectedDealershipId] =
    useSelectedDealership();
  const [displayData, SetDisplayData] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    getDealerListDropDown();
  }, []);

  useEffect(() => {
    if (selectedDealershipId) {
      fetchServiceDetails(selectedDealershipId);
    } else {
      SetDisplayData([]);
    }
  }, [selectedDealershipId]);

  useEffect(() => {
    const handleDealershipsChanged = () => {
      getDealerListDropDown();
      if (selectedDealershipId) {
        fetchServiceDetails(selectedDealershipId);
      }
    };
    const handleLocalStorageChange = (e) => {
      if (e.detail && e.detail.key === "dealershipsVersion") {
        getDealerListDropDown();
        if (selectedDealershipId) {
          fetchServiceDetails(selectedDealershipId);
        }
      }
    };
    const handleStorage = (e) => {
      if (e.key === "dealershipsVersion") {
        getDealerListDropDown();
        if (selectedDealershipId) {
          fetchServiceDetails(selectedDealershipId);
        }
      }
    };
    window.addEventListener("dealershipsChanged", handleDealershipsChanged);
    window.addEventListener("localStorageChange", handleLocalStorageChange);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        "dealershipsChanged",
        handleDealershipsChanged,
      );
      window.removeEventListener(
        "localStorageChange",
        handleLocalStorageChange,
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, [selectedDealershipId]);

  const getDealerListDropDown = async () => {
    try {
      const response = await apiManager.get(API_ENDPOINTS.DROPDOWN_DEALERSHIPS);
      if (response.status === 200) {
        const dataArray = response.data;
        setDealerships(dataArray);

        if (dataArray && dataArray.length > 0 && !selectedDealershipId) {
          const firstDealership = dataArray[0];
          setSelectedDealershipId(String(firstDealership.id));
        } else if (selectedDealershipId) {
          const exists = dataArray.some(
            (d) => String(d.id) === String(selectedDealershipId),
          );
          if (!exists) {
            if (dataArray.length > 0) {
              setSelectedDealershipId(String(dataArray[0].id));
            } else {
              setSelectedDealershipId("");
            }
          } else {
            fetchServiceDetails(selectedDealershipId);
          }
        }
      } else {
        setDealerships([]);
        showErrorMessage(localization("failed_fetch_dealerships"));
      }
    } catch (error) {
      setDealerships([]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const { instance } = useMsal();

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("accessTokenExpiry");
    localStorage.removeItem("userRole");
    localStorage.removeItem("tenantId");
    try {
      setSelectedDealershipId(null);
    } catch (_) {}
    // Only trigger MSAL logout for SSO users — external users have no MSAL session
    if (instance.getAllAccounts().length > 0) {
      instance.logoutRedirect({ postLogoutRedirectUri: "/" });
    } else {
      navigate("/");
    }
  };

  const fetchServiceDetails = async (id) => {
    try {
      const response = await apiManager.post(API_ENDPOINTS.DEALER_GET_ID, {
        id: Number(id),
      });
      if (response.status === 200) {
        const data = response.data;
        SetDisplayData(data);
      } else {
        showErrorMessage(localization("failed_fetch_service_details"));
      }
    } catch (error) {
      showErrorMessage(error || localization("failed_fetch_service_details"));
    }
  };

  return (
    <div>
      <header className="main-header d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <div
            className="logo-container"
            style={{ cursor: "pointer" }}
            onClick={() => {
              const userRole = getUserRoleFromJWT();
              if (userRole === UserRole.DEALER_ADMIN) {
                navigate("/admin-dashboard");
              } else {
                navigate("/service-desk-dashboard");
              }
            }}
          >
            <img
              className="fusz-logo"
              src={LOGO_URL}
              alt={localization("lou_fusz_logo_alt")}
            />
          </div>
          <div className="d-flex flex-column company-details">
            <strong className="company-title">{displayData?.name}</strong>
            <div className="company-address">
              <div>{displayData?.address1}</div>
              <div>
                {[
                  displayData?.cityOrLocality,
                  displayData?.stateOrProvinceCode
                    ? US_STATES.find(
                        (s) => s.code === displayData.stateOrProvinceCode,
                      )?.name || displayData.stateOrProvinceCode
                    : displayData?.state
                      ? US_STATES.find((s) => s.code === displayData.state)
                          ?.name || displayData.state
                      : "",
                  displayData?.postalCodeOrZip,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </div>
            </div>
          </div>

          <img
            className="dealer-logo"
            src={
              displayData?.logoPath ||
              "../assets/images/logo/no-logo-placeholder.svg"
            }
            onError={(e) => {
              const fallback = "../assets/images/logo/no-logo-placeholder.svg";
              if (e.currentTarget.src !== window.location.origin + fallback) {
                e.currentTarget.src = fallback;
              }
            }}
            alt={localization("logo_alt")}
          />
        </div>
        <div className="d-flex align-items-center gap-3">
          {(() => {
            const isHomePage =
              location.pathname === "/admin-dashboard" ||
              location.pathname === "/service-desk-dashboard";

            if (!isHomePage) {
              const selectedDealership = dealerships.find(
                (d) => String(d.id) === String(selectedDealershipId),
              );
              return (
                <input
                  className="form-control"
                  value={
                    selectedDealership
                      ? selectedDealership.name
                      : localization("select_dealership_placeholder")
                  }
                  disabled
                  style={{ backgroundColor: "#e9ecef", fontWeight: "bold" }}
                />
              );
            } else {
              const userRole = getUserRoleFromJWT();
              const isServiceDeskUser = userRole === UserRole.SERVICE_DESK_USER;
              const isUnassignedServiceDesk =
                isServiceDeskUser && !selectedDealershipId;
              return (
                <select
                  className="form-select"
                  value={selectedDealershipId || ""}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    setSelectedDealershipId(String(selectedValue));
                  }}
                  disabled={dealerships.length === 0 || isServiceDeskUser}
                >
                  {isUnassignedServiceDesk && (
                    <option value="">
                      {localization("select_dealership_placeholder")}
                    </option>
                  )}
                  {dealerships.map((d, idx) => (
                    <option key={d.id || idx} value={String(d.id)}>
                      {d.name || localization("unknown_dealership")}
                    </option>
                  ))}
                </select>
              );
            }
          })()}
          <div
            className="d-flex align-items-center user-profile"
            ref={dropdownRef}
          >
            <div className="user-image">
              <img
                src="/assets/images/profile/user1.png"
                alt={localization("user_profile_alt")}
                className="img-circle"
              />
            </div>
            <div className="dropdown">
              <button
                className="dropdown-toggle"
                type="button"
                onClick={() => setOpen(!open)}
              >
                {getUserRoleFromJWT() === UserRole.DEALER_ADMIN
                  ? localization("shop_admin")
                  : localization("service_Desk_user")}{" "}
                <i className="fa-solid fa-chevron-down"></i>
              </button>
              <ul
                className={`dropdown-menu custom-dropdown-menu${
                  open ? " show" : ""
                }`}
                style={{ display: open ? "block" : "none" }}
              >
                <li>
                  <button
                    type="button"
                    className="dropdown-item d-flex align-items-center"
                    onClick={handleLogout}
                  >
                    <i className="fa-solid fa-arrow-right-from-bracket me-2"></i>
                    {localization("logout_button")}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </header>
    </div>
  );
};

export default Header;
